import QueryString from 'query-string';

import { ApiUrl, Call, ParseDataResponse } from '@api/ApiUtils';
import {
  CorporateCatalogItem,
  CorporateCatalogItemDetail,
  EventAnalyticsRow,
  EventSeriesAnalyticsSummary,
  EventSummary,
  ItemAnalyticsRow,
  PriceAgreement,
} from 'types/Agreements';
import { EventSeries, EventSeriesCadenceUnit, SchedulePeriodUnit } from 'types/EventSeries';
import { ID } from 'types/ID';
import { Pagy } from 'types/Pagy';

export interface FetchPriceAgreementsForEntityResponseBody {
  pagy?: Pagy;
  price_agreements: PriceAgreement[];
}

export interface UpdateCatalogItemParams {
  business_unit_id?: number | null;
  description?: string;
  name?: string;
  sku?: string | null;
  unit?: string;
}

export interface FetchCatalogItemsParams {
  items?: number;
  page?: number;
  search?: string;
}

export interface FetchCatalogItemsResponseBody {
  catalog_items: CorporateCatalogItem[];
  pagy: Pagy;
}

export interface FetchPriceAgreementsParams {
  items?: number;
  page?: number;
  search?: string;
  sort?: string;
  sort_order?: 'asc' | 'desc';
  status?: string;
}

export interface FetchPriceAgreementsResponseBody {
  pagy: Pagy;
  price_agreements: PriceAgreement[];
}

export interface FetchPriceAgreementResponseBody {
  price_agreement: PriceAgreement;
}

export interface CreatePriceAgreementParams {
  business_unit_id?: number | null;
  corporate_division_id: number;
  name: string;
  notice_threshold_days?: number;
  status: string;
}

export interface PriceAgreementSummary {
  business_units_with_agreements: number;
  event_series_count: number;
  total_price_agreements: number;
  total_value: number;
}

export interface FetchPriceAgreementSummaryResponseBody {
  summary: PriceAgreementSummary;
}

type ApiResponse<T> = Promise<{ data: T; success: boolean }>;

export interface CreateCatalogItemParams {
  business_unit_id?: number | null;
  description?: string;
  name: string;
  sku?: string | null;
  unit?: string;
}

export interface FetchCatalogItemResponseBody {
  catalog_item: CorporateCatalogItem;
}

export function createCatalogItemAsync (params: CreateCatalogItemParams): ApiResponse<FetchCatalogItemResponseBody> {
  return Call('post', ApiUrl('/agreements/catalog_items'), { catalog_item: params }, ParseDataResponse) as ApiResponse<FetchCatalogItemResponseBody>;
}

export function fetchCatalogItemsAsync (params?: FetchCatalogItemsParams): ApiResponse<FetchCatalogItemsResponseBody> {
  const qs = params ? `?${QueryString.stringify(params)}` : '';

  return Call('get', `${ApiUrl('/agreements/catalog_items')}${qs}`, {}, ParseDataResponse) as ApiResponse<FetchCatalogItemsResponseBody>;
}

export function fetchCatalogItemAsync (id: number | string): ApiResponse<CorporateCatalogItemDetail> {
  return Call('get', ApiUrl(`/agreements/catalog_items/${encodeURIComponent(String(id))}`), {}, ParseDataResponse) as ApiResponse<CorporateCatalogItemDetail>;
}

export function fetchPriceAgreementsForSupplierAsync (
  supplierId: number | string
): ApiResponse<FetchPriceAgreementsForEntityResponseBody> {
  const url = ApiUrl(`/corporate/suppliers/${encodeURIComponent(String(supplierId))}/price_agreements`);

  return Call('get', url, {}, ParseDataResponse) as ApiResponse<FetchPriceAgreementsForEntityResponseBody>;
}

export function fetchPriceAgreementsForDivisionAsync (
  divisionId: number | string
): ApiResponse<FetchPriceAgreementsForEntityResponseBody> {
  const url = ApiUrl(`/corporate/divisions/${encodeURIComponent(String(divisionId))}/price_agreements`);

  return Call('get', url, {}, ParseDataResponse) as ApiResponse<FetchPriceAgreementsForEntityResponseBody>;
}

export function updateCatalogItemAsync (
  id: number | string,
  params: UpdateCatalogItemParams
): ApiResponse<CorporateCatalogItemDetail> {
  const url = ApiUrl(`/agreements/catalog_items/${encodeURIComponent(String(id))}`);

  return Call('patch', url, { catalog_item: params }, ParseDataResponse) as ApiResponse<CorporateCatalogItemDetail>;
}

export function fetchPriceAgreementsAsync (params?: FetchPriceAgreementsParams): ApiResponse<FetchPriceAgreementsResponseBody> {
  const qs = params ? `?${QueryString.stringify(params)}` : '';

  return Call('get', `${ApiUrl('/agreements/price_agreements')}${qs}`, {}, ParseDataResponse) as ApiResponse<FetchPriceAgreementsResponseBody>;
}

export function fetchPriceAgreementAsync (id: number | string): ApiResponse<FetchPriceAgreementResponseBody> {
  return Call('get', ApiUrl(`/agreements/price_agreements/${encodeURIComponent(String(id))}`), {}, ParseDataResponse) as ApiResponse<FetchPriceAgreementResponseBody>;
}

export function createPriceAgreementAsync (params: CreatePriceAgreementParams): ApiResponse<FetchPriceAgreementResponseBody> {
  return Call('post', ApiUrl('/agreements/price_agreements'), { price_agreement: params }, ParseDataResponse) as ApiResponse<FetchPriceAgreementResponseBody>;
}

export function getPriceAgreementSummary (): ApiResponse<FetchPriceAgreementSummaryResponseBody> {
  return Call('get', ApiUrl('/agreements/price_agreements/summary'), {}, ParseDataResponse) as ApiResponse<FetchPriceAgreementSummaryResponseBody>;
}

export interface PriceUpdateExport {
  download_url: string | null;
  id: number;
  status: 'building' | 'completed' | 'error' | 'pending' | 'queuing';
}

export interface FetchPriceUpdateExportResponseBody {
  export: PriceUpdateExport;
}

export interface CreatePriceUpdateExportParams {
  price_agreement_ids: number[];
}

export function createPriceUpdateExportAsync (params: CreatePriceUpdateExportParams): ApiResponse<FetchPriceUpdateExportResponseBody> {
  return Call('post', ApiUrl('/agreements/price_update_exports'), params, ParseDataResponse) as ApiResponse<FetchPriceUpdateExportResponseBody>;
}

export type UpdatePriceAgreementParams = Partial<CreatePriceAgreementParams>;

export function updatePriceAgreementAsync (
  id: number | string,
  params: UpdatePriceAgreementParams
): ApiResponse<FetchPriceAgreementResponseBody> {
  const url = ApiUrl(`/agreements/price_agreements/${encodeURIComponent(String(id))}`);

  return Call('patch', url, { price_agreement: params }, ParseDataResponse) as ApiResponse<FetchPriceAgreementResponseBody>;
}

export function deletePriceAgreementAsync (id: number | string): ApiResponse<void> {
  return Call('delete', ApiUrl(`/agreements/price_agreements/${encodeURIComponent(String(id))}`), {}, ParseDataResponse) as ApiResponse<void>;
}

// Event Series (direct /event_series REST endpoints)
export interface FetchEventSeriesListParams {
  items?: number;
  page?: number;
  search?: string;
}

export interface FetchEventSeriesListResponseBody {
  event_series: EventSeries[];
  pagy: Pagy;
}

export function fetchEventSeriesListAsync (
  priceAgreementId: ID,
  params: FetchEventSeriesListParams = {}
): ApiResponse<FetchEventSeriesListResponseBody> {
  const qs = QueryString.stringify({ price_agreement_id: priceAgreementId, ...params });
  const url = `${ApiUrl('/event_series')}?${qs}`;

  return Call('get', url, {}, ParseDataResponse) as ApiResponse<FetchEventSeriesListResponseBody>;
}

export interface UpdateEventSeriesParams {
  baseline_mode?: 'last_agreed_price' | 'lowest_recent_price';
  cadence?: number;
  cadence_unit?: EventSeriesCadenceUnit;
  initial_submission_unit?: SchedulePeriodUnit;
  initial_submission_value?: number | null;
  name?: string;
  review_unit?: SchedulePeriodUnit;
  review_value?: number | null;
}

export function updateEventSeriesAsync (
  eventSeriesId: number | string,
  params: UpdateEventSeriesParams
): ApiResponse<{ event_series: EventSeries }> {
  const url = ApiUrl(`/event_series/${encodeURIComponent(String(eventSeriesId))}`);

  return Call('patch', url, { event_series: params }, ParseDataResponse) as ApiResponse<{ event_series: EventSeries }>;
}

export function deleteEventSeriesAsync (eventSeriesId: number | string): ApiResponse<void> {
  const url = ApiUrl(`/event_series/${encodeURIComponent(String(eventSeriesId))}`);

  return Call('delete', url, {}, ParseDataResponse) as ApiResponse<void>;
}

// Event Series Analytics
export interface PagyParams {
  items?: number;
  page?: number;
  search?: string;
}

export interface FetchEventSeriesAnalyticsSummaryResponseBody {
  summary: EventSeriesAnalyticsSummary;
}

export interface FetchEventSeriesEventsResponseBody {
  events: EventAnalyticsRow[];
  pagy: Pagy;
}

export interface FetchEventSeriesItemsResponseBody {
  items: ItemAnalyticsRow[];
  pagy: Pagy;
}

// Event Series analytics endpoints (scoped by event_series_id, from TASK-036a)
export function getEventSeriesAnalyticsSummaryById (eventSeriesId: number | string): ApiResponse<FetchEventSeriesAnalyticsSummaryResponseBody> {
  const url = ApiUrl(`/event_series/${encodeURIComponent(String(eventSeriesId))}/analytics/summary`);

  return Call('get', url, {}, ParseDataResponse) as ApiResponse<FetchEventSeriesAnalyticsSummaryResponseBody>;
}

export function getEventSeriesEventsById (eventSeriesId: number | string, params: PagyParams = {}): ApiResponse<FetchEventSeriesEventsResponseBody> {
  const qs = Object.keys(params).length > 0 ? `?${QueryString.stringify(params)}` : '';
  const url = ApiUrl(`/event_series/${encodeURIComponent(String(eventSeriesId))}/analytics/events`);

  return Call('get', `${url}${qs}`, {}, ParseDataResponse) as ApiResponse<FetchEventSeriesEventsResponseBody>;
}

export function getEventSeriesItemsById (eventSeriesId: number | string, params: PagyParams = {}): ApiResponse<FetchEventSeriesItemsResponseBody> {
  const qs = Object.keys(params).length > 0 ? `?${QueryString.stringify(params)}` : '';
  const url = ApiUrl(`/event_series/${encodeURIComponent(String(eventSeriesId))}/analytics/items`);

  return Call('get', `${url}${qs}`, {}, ParseDataResponse) as ApiResponse<FetchEventSeriesItemsResponseBody>;
}

// Event-level analytics (per BidRequest)
export interface FetchEventSummaryResponseBody {
  summary: EventSummary;
}

export interface FetchEventItemsResponseBody {
  items: ItemAnalyticsRow[];
  pagy: Pagy;
}

export function getEventSummary (bidRequestId: number | string): ApiResponse<FetchEventSummaryResponseBody> {
  const url = ApiUrl(`/agreements/events/${encodeURIComponent(String(bidRequestId))}/summary`);

  return Call('get', url, {}, ParseDataResponse) as ApiResponse<FetchEventSummaryResponseBody>;
}

export function getEventItems (bidRequestId: number | string, params: PagyParams = {}): ApiResponse<FetchEventItemsResponseBody> {
  const qs = Object.keys(params).length > 0 ? `?${QueryString.stringify(params)}` : '';
  const url = ApiUrl(`/agreements/events/${encodeURIComponent(String(bidRequestId))}/items`);

  return Call('get', `${url}${qs}`, {}, ParseDataResponse) as ApiResponse<FetchEventItemsResponseBody>;
}
